import { useMemo } from 'react';
import { supabaseCoreClient } from '../supabase.ts';
import { callRpc } from '../supabase-operations.js';
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { encode } from "@supabase-cache-helpers/postgrest-react-query";
import { queryClient } from '../constants.js';
import { commonQueryProps } from './cache.js';
import type { PicklistInfo, ProcessedPicklistMetadata } from '../../types/metadata.js';
import type { Database } from '../../types/database.types.js';
import type { CachedQueryResult } from '../../types/supabase.js';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

type RawPicklistInfo = Database['core_crm']['Views']['picklist_view']['Row'];

/**
 * Builds the Supabase query for fetching picklist metadata
 * @returns PostgrestBuilder query for picklist_view with all necessary columns
 */
export const buildQueryForPicklists = () => {
  return supabaseCoreClient
    .from('picklist_view')
    .select('*');
};

/**
 * Post-processes picklist data by grouping by table and column name
 * @param data - Raw picklist data from database
 * @returns Processed picklist metadata grouped by table
 */
export const postProcessPicklists = (data: RawPicklistInfo[]): ProcessedPicklistMetadata => {
  const validPicklists = data.filter((picklist): picklist is PicklistInfo =>
    picklist.id !== null && Boolean(picklist.table_name?.trim()) && Boolean(picklist.column_name?.trim()));
  const groupedByTable: ProcessedPicklistMetadata = {
    flattened: {},
    byId: {},
    byTableName: {},
    asList: validPicklists
  };

  validPicklists.forEach(picklist => {
    const tableName = picklist.table_name;
    if (!groupedByTable.byTableName[tableName]) {
      groupedByTable.byTableName[tableName] = { asList: [], byFieldName: {} };
    }
    groupedByTable.byTableName[tableName].asList.push(picklist);
    groupedByTable.byTableName[tableName].byFieldName[picklist.column_name] = picklist;
    groupedByTable.flattened[`${picklist.table_name}_${picklist.id}`] = picklist;
    groupedByTable.byId[picklist.id] = picklist;
    
    // Also add table-level access for backward compatibility
    if (!(groupedByTable as any)[tableName]) {
      (groupedByTable as any)[tableName] = groupedByTable.byTableName[tableName];
    }
  });

  return groupedByTable;
};

// Result type for picklist operations
interface PicklistResult {
  data: ProcessedPicklistMetadata | null;
  error: any;
}

/**
 * Fetches picklist data outside of React context
 * @returns Promise with processed picklist metadata or error
 */
export const getPicklists = async (): Promise<PicklistResult> => {
  const query = buildQueryForPicklists();
  let picklistData: RawPicklistInfo[] | null = null;
  let picklistError: any = null;
  
  try {
    // TypeScript cannot infer the Supabase cache structure - see SupabaseCacheData type definition
    const cachedResult = queryClient.getQueryData(encode(query, false)) as CachedQueryResult<RawPicklistInfo[]>;
    picklistData = cachedResult?.data ?? null;
  } catch {
    // Cache miss or error, will fetch from API
  }
  
  if (!picklistData) {
    const { data, error } = await query;
    picklistData = data;
    picklistError = error;
  }

  if (picklistError) {
    return { data: null, error: picklistError };
  }

  const processedData = postProcessPicklists(picklistData ?? []);
  return { data: processedData, error: null };
};

// Hook options interface
interface UsePicklistsOptions {
  enabled?: boolean;
}

// Hook result interface
interface UsePicklistsResult {
  data: ProcessedPicklistMetadata | null;
  error: any;
  isLoading: boolean;
}

/**
 * React hook to fetch picklist data using React Query
 * @param options - Options including enabled flag
 * @returns Picklist data with loading and error states
 */
export const usePicklists = ({ enabled }: UsePicklistsOptions): UsePicklistsResult => {
  const bootReady = useMetadataBootReady();
  const query = buildQueryForPicklists();

  const { data, error, isPending } = useQuery(
    query,
    {
      ...commonQueryProps,
      enabled: enabled && bootReady,
      meta: persistedRelations('core_crm.picklist_view')
    }
  );

  // Memoized on the query result so the processed object keeps a stable
  // identity across renders — consumers memoize off it.
  const processedData = useMemo(() => (data ? postProcessPicklists(data) : null), [data]);

  if (isPending || error || !data) {
    return {
      data: null,
      error,
      isLoading: isPending
    };
  }

  return {
    data: processedData,
    error: null,
    isLoading: false
  };
};

// Interface for update/create picklist parameters
interface UpdateOrCreatePicklistParams {
  schema: string;
  tableName: string;
  columnName: string;
  values: string[];
  picklistType: string;
}

/**
 * Creates or updates a picklist and its values
 * @param params - Picklist creation/update parameters
 * @returns Created or updated picklist record
 */
export const updateOrCreatePicklist = async ({ 
  schema, 
  tableName, 
  columnName, 
  values, 
  picklistType 
}: UpdateOrCreatePicklistParams): Promise<any> => {
    const { data } = await callRpc({
        schema: 'core_crm',
        functionName: 'update_or_create_picklist_and_values',
        params: {
            p_schema_name: schema,
            p_table_name: tableName,
            p_column_name: columnName,
            p_values: values,
            p_picklist_type: picklistType,
        }
    });
    // we can't define return type as a record, only a table of records. Our function only returns one record.
    return data[0];
};

interface AddPicklistValueParams {
  schema: string;
  tableName: string;
  columnName: string;
  value: string;
}

/**
 * Adds one option to a non-system picklist (capability add_picklist_values).
 * @returns The stored option, which is the existing one when the name matches it ignoring case
 */
export const addPicklistValue = async ({ schema, tableName, columnName, value }: AddPicklistValueParams): Promise<string> => {
    const { data } = await callRpc({
        schema: 'core_crm',
        functionName: 'add_picklist_value',
        params: {
            p_schema_name: schema,
            p_table_name: tableName,
            p_column_name: columnName,
            p_value: value,
        }
    });
    return data as string;
};

// Interface for create picklist field parameters
interface CreatePicklistFieldParams {
  tableInfoId: number;
  columnName: string;
  uiLabel: string;
  values: string[];
  picklistType: string;
  isRequired: boolean;
  isUnique: boolean;
  isIndexed: boolean;
  fieldHelp?: string;
  defaultValue?: any;
}

/**
 * Creates a new picklist field with associated metadata
 * @param params - Picklist field creation parameters
 * @returns Created picklist field record
 */
export const createPicklistField = async ({ 
  tableInfoId, 
  columnName, 
  uiLabel, 
  values, 
  picklistType, 
  isRequired, 
  isUnique, 
  isIndexed, 
  fieldHelp,
  defaultValue
}: CreatePicklistFieldParams): Promise<any> => {
    const { data } = await callRpc({
        schema: 'core_crm',
        functionName: 'create_picklist_field',
        params: {
            p_table_info_id: tableInfoId,
            p_column_name: columnName,
            p_ui_label: uiLabel,
            p_values: values,
            p_picklist_type: picklistType,
            p_field_is_required: isRequired,
            p_field_is_unique: isUnique,
            p_field_is_indexed: isIndexed,
            p_field_help: fieldHelp,
            p_default_value: defaultValue !== undefined ? { value: defaultValue } : null
        }
    });
    return data[0];
};
